import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { SkeletonTile } from "./Skeleton";
import { Tile, type TileProps } from "./Tile";

interface TilesSectionProps {
  cards: TileProps[];
  hasHero?: boolean;
  empty?: React.ReactNode;
  title?: string;
  cardsPerRow?: { sm?: number; md?: number; lg?: number };
  cardHeight?: {
    sm?: string | number;
    md?: string | number;
    lg?: string | number;
  };
  initialPaginationCount?: number;
  paginationSteps?: number;
  enableFakeLoading?: boolean;
  fakeLoadMs?: number;
  afterSection?: React.ReactNode;
}

export function TilesSection({
  cards,
  hasHero,
  empty,
  title,
  cardsPerRow,
  cardHeight,
  initialPaginationCount = 5,
  paginationSteps = 4,
  enableFakeLoading,
  fakeLoadMs = 600,
  afterSection,
}: TilesSectionProps) {
  const [visibleItemsCount, setVisibleItemsCount] = useState(
    initialPaginationCount,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visibleCards = useMemo(
    () => cards.slice(0, Math.min(visibleItemsCount, cards.length)),
    [cards, visibleItemsCount],
  );

  const remaining = useMemo(
    () => Math.max(0, cards.length - visibleCards.length),
    [cards.length, visibleCards.length],
  );
  const canSeeMore = useMemo(() => remaining > 0, [remaining]);
  const pendingAdd = useMemo(
    () => Math.min(paginationSteps, remaining),
    [paginationSteps, remaining],
  );

  const handleSeeMore = () => {
    if (isLoading) return;

    try {
      setError(null);

      if (enableFakeLoading) {
        setIsLoading(true);
        timeoutRef.current = setTimeout(() => {
          setIsLoading(false);
          setVisibleItemsCount((prev) =>
            Math.min(prev + paginationSteps, cards.length),
          );
        }, fakeLoadMs);
      } else {
        setVisibleItemsCount((prev) =>
          Math.min(prev + paginationSteps, cards.length),
        );
      }
    } catch {
      setIsLoading(false);
      setError("Failed to load more items. Please try again.");
    }
  };

  useEffect(() => {
    setVisibleItemsCount(initialPaginationCount);
  }, [cards, initialPaginationCount]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return (
    <Stack alignItems={"center"} spacing={3} width={"100%"}>
      <Stack alignItems={"flex-start"} gap={3} width={"100%"}>
        {title && (
          <Typography component={"h4"} variant="h4">
            {title}
          </Typography>
        )}
        {visibleCards.length > 0 ? (
          <>
            <Grid
              container
              alignItems="stretch"
              spacing={{ sm: 2, md: 3 }}
              columns={{
                sm: cardsPerRow?.sm ?? 1,
                md: cardsPerRow?.md ?? 2,
                lg: cardsPerRow?.lg ?? 2,
              }}
              justifyContent="flex-start"
              sx={{
                width: "100%",
                maxWidth: 1280,
                alignItems: "stretch",
                "& > .MuiGrid-item": { alignSelf: "stretch" },
              }}
            >
              {visibleCards.map((card, index) => {
                const isHero = hasHero && index === 0;
                return (
                  <Grid
                    size={{
                      sm: 2,
                      md: isHero ? 3 : 1,
                      lg: isHero ? 3 : 1,
                    }}
                    key={card.title}
                    alignItems={"stretch"}
                    sx={{
                      width: "100%",
                      height: {
                        sm: cardHeight?.sm ?? "auto",
                        md: cardHeight?.md ?? "auto",
                        lg: cardHeight?.lg ?? (isHero ? "auto" : 405),
                      },
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    <Tile key={card.id} {...card} isHero={isHero} />
                  </Grid>
                );
              })}
              {isLoading &&
                Array.from({ length: pendingAdd }).map((_, index) => (
                  <Grid
                    size={{
                      sm: 2,
                      md: 1,
                      lg: 1,
                    }}
                    // eslint-disable-next-line react-x/no-array-index-key, @typescript-eslint/restrict-template-expressions
                    key={`loading-skeleton-${index}`}
                    alignItems={"stretch"}
                    sx={{
                      width: "100%",
                      height: {
                        sm: cardHeight?.sm ?? "auto",
                        md: cardHeight?.md ?? "auto",
                        lg: cardHeight?.lg ?? 405,
                      },
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    <SkeletonTile />
                  </Grid>
                ))}
            </Grid>
            {afterSection}
          </>
        ) : (
          (empty ?? <Typography variant="h4">No content available.</Typography>)
        )}
      </Stack>
      {error && (
        <Typography variant="body2" color="error" mt={1}>
          {error}
        </Typography>
      )}
      {!isLoading && canSeeMore && (
        <Button
          variant="outlined"
          color={"inherit"}
          sx={{
            color: "var(--mui-palette-primary-contrastText) !important",
            width: { sm: "100%", md: "auto" },
          }}
          onClick={handleSeeMore}
        >
          Load More
        </Button>
      )}
    </Stack>
  );
}
